import React, { createContext, useContext, useEffect, useState } from 'react'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import NotesIcon from '@mui/icons-material/Notes'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import API from '../../../services/API'
import OrganisationRequester from './OrganisationRequester'
import Patient from './Patient'
import Submit from './Submit'
import PickupDropoff from './PickupDropoff'
import ReturnJourney from './ReturnJourney'
import Recurrence from './Recurrence'
import BillingPreview from './BillingPreview'
import { Chip, Grid } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import Assignment from './Assignment'
import Eligibility from './Eligibility'
import DischargeCrewSection from './DischargeCrewSection'

export const SectionHeader = ({ icon, title }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
    <Avatar sx={{ bgcolor: 'primary.main', width: 28, height: 28 }}>
      {icon}
    </Avatar>
    <Typography variant="h6" fontWeight="600">
      {title}
    </Typography>
  </Stack>
)

export const BookingContext = createContext({})

const getId = (value) => (typeof value === 'string' ? value : value?._id)

const NewPTSBooking = ({
  existingBooking,
  setEditMode,
  loadBooking,
  portalMode = false,
  portalContext = {},
  onSubmitted,
}) => {
  const [formData, setFormData] = useState(
    existingBooking ?? {
      source: portalMode ? 'Hospital Portal' : 'Portal',
      organisation: portalContext?.organisation || null,
      requestedBy: portalMode && portalContext?.user?._id
        ? [{ model: 'User', _id: portalContext.user._id }]
        : [],
      patient: null,
      type: '',
      priority: 'P3',
      recurrence: {
        enabled: false,
        rule: '',
        until: null,
        frequency: '',
        interval: 1,
        daysOfWeek: [],
      },
      status: 'draft',
      patientIsCustomer: false,
      wardDepartment:
        portalContext?.ward?._id ||
        portalContext?.ward?.id ||
        portalContext?.ward ||
        null,
      returnJourney: {
        required: false,
      },
      pickup: {
        locationType: 'Patient Address',
      },
      dropoff: {
        locationType: 'Patient Address',
      },
    },
  )

  const [selectedPatient, setSelectedPatient] = useState(
    existingBooking?.patient ?? null,
  )

  const [locationTypeSelected, setLocationTypeSelected] = useState(false)

  // Whether the requesting organisation has any wards/departments — a ward
  // is only a required field when there's one to pick (some customer orgs,
  // e.g. repatriation brokers, have none). True until the selector learns
  // otherwise so the requirement never flickers off while loading.
  const [orgHasWards, setOrgHasWards] = useState(true)

  // Base location drives base-to-base mileage billing. With one base it's
  // stamped silently; with several the co-ordinator must choose (required)
  const [baseOptions, setBaseOptions] = useState([])
  useEffect(() => {
    if (portalMode) return
    API.get('/base-location-options')
      .then((res) => {
        if (res.status !== 200) return
        const options = res.data || []
        setBaseOptions(options)
        if (options.length === 1) {
          setFormData((prev) =>
            prev.baseLocation
              ? prev
              : { ...prev, baseLocation: options[0]._id },
          )
        }
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalMode])

  useEffect(() => {
    if (!portalMode) return

    setFormData((prev) => ({
      ...prev,
      source: 'Hospital Portal',
      organisation: portalContext?.organisation || null,
      wardDepartment:
        portalContext?.ward?._id ||
        portalContext?.ward?.id ||
        portalContext?.ward ||
        null,
      requestedBy: portalContext?.user?._id
        ? [{ model: 'User', _id: portalContext.user._id }]
        : [],
      patientIsCustomer: false,
    }))
  }, [portalMode, portalContext?.organisation, portalContext?.ward, portalContext?.user])

  useEffect(() => {
    if (existingBooking?.journeys?.length) {
      setFormData((prev) => ({
        ...prev,
        journeyType: existingBooking.journeys[0].journeyType,
        dropoff: existingBooking.journeys[0].dropoff,
        pickup: existingBooking.journeys[0].pickup,
      }))
    }
    if (existingBooking?.journeys?.[0]?.returnJourney) {
      setFormData((prev) => ({
        ...prev,
        returnJourney: {
          required: true,
          pickup: {
            when: {
              patientReadyFrom:
                existingBooking.journeys[1].pickup?.when?.patientReadyFrom ||
                null,
            },
          },
        },
      }))
    }
  }, [existingBooking])

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const keys = field.split('.')
      const newData = { ...prev }
      let temp = newData

      keys.forEach((key, index) => {
        if (index === keys.length - 1) {
          temp[key] = value
        } else {
          temp[key] = { ...temp[key] } || {}
          temp = temp[key]
        }
      })

      return newData
    })
  }

  return (
    <Box>
      <BookingContext.Provider
        value={{
          formData,
          setFormData,
          handleChange,
          selectedPatient,
          setSelectedPatient,
          existingBooking,
          locationTypeSelected,
          setLocationTypeSelected,
          baseOptions,
          orgHasWards,
          setOrgHasWards,
        }}
      >
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f9fafc 100%)',
            p: 3,
          }}
        >
          <CardContent>
            <Stack spacing={4}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
              >
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Stack>
                      <Typography variant="title2">
                        {existingBooking ? 'Edit' : 'New'} Patient Transport
                        Booking
                      </Typography>
                      {existingBooking && formData?.bookingRef && (
                        <Box>
                          <Chip
                            label={formData.bookingRef}
                            sx={{ borderRadius: 1 }}
                            variant="filled"
                            color="contrast"
                            size="medium"
                          />
                        </Box>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </Stack>
              <Stack gap={4} divider={<Divider />}>
                <Box id="booking-section-patient">
                  <Patient
                    existingBooking={!!existingBooking}
                    portalMode={portalMode}
                    portalOrganisation={portalContext?.organisation}
                  />
                </Box>
                {portalMode ? (
                  <Box>
                    <SectionHeader
                      icon={<NotesIcon fontSize="small" />}
                      title="Requesting Hospital"
                    />
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: 2,
                        bgcolor: 'background.default',
                      }}
                    >
                      <Typography fontWeight={900}>
                        {portalContext?.organisation?.orgName ||
                          'Hospital portal'}
                      </Typography>
                      {portalContext?.wardOptions?.length > 1 ? (
                        <Autocomplete
                          sx={{ mt: 1.5, maxWidth: 520 }}
                          options={portalContext.wardOptions}
                          value={
                            portalContext.wardOptions.find(
                              (ward) =>
                                (ward._id || ward.id) ===
                                (formData?.wardDepartment?._id ||
                                  formData?.wardDepartment),
                            ) || null
                          }
                          onChange={(event, ward) =>
                            setFormData((prev) => ({
                              ...prev,
                              wardDepartment: ward?._id || ward?.id || null,
                            }))
                          }
                          getOptionLabel={(option) =>
                            option?.label ||
                            [option?.name, option?.internalLocation]
                              .filter(Boolean)
                              .join(' - ')
                          }
                          isOptionEqualToValue={(option, value) =>
                            (option?._id || option?.id) ===
                            (value?._id || value?.id)
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Requesting ward"
                              required
                            />
                          )}
                        />
                      ) : (
                        <Typography color="text.secondary">
                          {portalContext?.ward?.label ||
                            portalContext?.ward?.name ||
                            'Selected ward'}
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        Requested by{' '}
                        {[portalContext?.user?.firstName, portalContext?.user?.lastName]
                          .filter(Boolean)
                          .join(' ') || 'current portal user'}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box id="booking-section-requester">
                    <OrganisationRequester />
                  </Box>
                )}
                {!portalMode && (
                <Box id="booking-section-billing">
                  <SectionHeader
                    icon={<AccountBalanceIcon fontSize="small" />}
                    title="Billing, Quotes & Invoices"
                  />
                  <BillingPreview
                    organisationId={getId(formData?.organisation)}
                    patientIsCustomer={Boolean(formData?.patientIsCustomer)}
                  />
                  {!existingBooking && baseOptions.length > 1 && (
                    <Autocomplete
                      sx={{ mt: 2, maxWidth: 460 }}
                      options={baseOptions}
                      // Commit the highlighted match on blur — typed-but-not-
                      // clicked otherwise leaves the value unset while the
                      // input still shows the base's name.
                      autoSelect
                      value={
                        baseOptions.find(
                          (base) =>
                            String(base._id) ===
                            String(
                              formData?.baseLocation?._id ||
                                formData?.baseLocation,
                            ),
                        ) || null
                      }
                      onChange={(e, base) =>
                        handleChange('baseLocation', base?._id || null)
                      }
                      getOptionLabel={(option) => option?.name || ''}
                      isOptionEqualToValue={(option, value) =>
                        String(option?._id) === String(value?._id)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Base Location"
                          required
                          helperText="Used for base-to-base mileage billing - journeys are billed from and back to this base"
                        />
                      )}
                    />
                  )}
                </Box>
                )}
                <Box id="booking-section-pickupdropoff">
                  <PickupDropoff />
                </Box>
                {!portalMode &&
                  !existingBooking &&
                  formData?.journeyType !== 'Risk Assessment' && (
                    <Box id="booking-section-return">
                      <ReturnJourney />
                    </Box>
                  )}
                {!portalMode && !existingBooking && <Recurrence />}
                {!portalMode && !existingBooking && (
                  <Box id="booking-section-assignment">
                    <Assignment />
                  </Box>
                )}
                {portalMode && !existingBooking && formData?.journeyType && (
                  <DischargeCrewSection
                    quietWhenNone={formData.journeyType !== 'Discharge'}
                  />
                )}
                {portalMode && !existingBooking && <Eligibility />}
                <Box>
                  <SectionHeader
                    icon={<NotesIcon fontSize="small" />}
                    title="Notes"
                  />
                  <TextField
                    label="Notes"
                    multiline
                    rows={3}
                    fullWidth
                    onChange={(e) =>
                      handleChange('bookingNotes', e.target.value)
                    }
                    value={formData.bookingNotes ?? ''}
                  />
                </Box>
              </Stack>
              <Submit
                setEditMode={setEditMode}
                loadBooking={loadBooking}
                onSubmitted={onSubmitted}
              />
            </Stack>
          </CardContent>
        </Card>
      </BookingContext.Provider>
    </Box>
  )
}

export default NewPTSBooking
